/* Feed pages behaviour (front page, /news, /hot, /top, /topic/:tag, /topics).
   The pages are complete without it (base.js rewrites ages and dates in the
   reader's clock). This adds: story art presentation once an image loads (logos on a card, blank
   or broken art dropped), the topics filter, and the chain widget's chart details (base.js pauses the
   live bands while the tab is hidden). */
(() => {
  'use strict'
  const doc = document
  const $ = (sel, el = doc) => el.querySelector(sel)
  const $$ = (sel, el = doc) => Array.from(el.querySelectorAll(sel))
  let strings = {}
  try {
    strings = JSON.parse($('#pxke-strings')?.textContent || '{}')
  } catch (err) {
    console.warn('[feed] strings not readable:', err)
  }

  /* -- story art (web/src/lib/imagePlate.ts) --------------------------------
     Sampling runs off the load event: images queue up and are inspected in
     idle-time batches; the pixels come from createImageBitmap scaled to
     32x32 (decoded off the main thread), so the main thread only reads a
     32x32 canvas. createImageBitmap(img) also gives the file's own pixel size:
     with a srcset, naturalWidth is density-corrected (the slot width, not the
     file's). */
  const SAMPLE = 32
  const DARK_PLATE_LUMINANCE = 0.78
  let sampler = null
  const samplerCtx = () => {
    if (!sampler) {
      const canvas = doc.createElement('canvas')
      canvas.width = SAMPLE
      canvas.height = SAMPLE
      sampler = canvas.getContext('2d', { willReadFrequently: true })
    }
    return sampler
  }
  /* The image's own size and a 32x32 RGBA sample, or null when it cannot be read. */
  const readArt = async (img) => {
    const ctx = samplerCtx()
    if (!ctx) return null
    if (typeof createImageBitmap === 'function') {
      let full = null
      let small = null
      try {
        full = await createImageBitmap(img)
        small = await createImageBitmap(full, { resizeWidth: SAMPLE, resizeHeight: SAMPLE, resizeQuality: 'low' })
        ctx.clearRect(0, 0, SAMPLE, SAMPLE)
        ctx.drawImage(small, 0, 0, SAMPLE, SAMPLE)
        return { w: full.width, h: full.height, data: ctx.getImageData(0, 0, SAMPLE, SAMPLE).data }
      } catch {
        /* resize options unsupported or a tainted image: the canvas path below */
      } finally {
        full?.close()
        small?.close()
      }
    }
    try {
      ctx.clearRect(0, 0, SAMPLE, SAMPLE)
      ctx.drawImage(img, 0, 0, SAMPLE, SAMPLE)
      return { w: img.naturalWidth, h: img.naturalHeight, data: ctx.getImageData(0, 0, SAMPLE, SAMPLE).data }
    } catch {
      return { w: img.naturalWidth, h: img.naturalHeight, data: null }
    }
  }
  const inspectArt = async (img) => {
    const art = await readArt(img)
    const w = art ? art.w : img.naturalWidth
    const h = art ? art.h : img.naturalHeight
    if (w < 16 || h < 16) return { kind: 'blank' }
    const ratio = w / h
    const fallback = { kind: 'art', opaque: false, dark: false, ratio, width: w, height: h }
    const data = art?.data
    if (!data) return fallback
    let opaque = 0
    let transparent = 0
    let lum = 0
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 32) {
        transparent++
        continue
      }
      opaque++
      lum += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255
    }
    const total = data.length / 4
    if (opaque / total < 0.01) return { kind: 'blank' }
    if (transparent / total <= 0.02) return { kind: 'art', opaque: true, dark: false, ratio, width: w, height: h }
    return { kind: 'art', opaque: false, dark: lum / opaque > DARK_PLATE_LUMINANCE, ratio, width: w, height: h }
  }
  /* art-classifier:start
     How a framed story image shows: 'photo' fills the tile (cover), 'whole'
     fits a much wider image, 'card' centres a logo or an icon on a tint.
     fit: { opaque, ratio, width, height } of the file served; requested: the
     width asked of the image proxy (&w=, 0 when unknown); logoUrl: the URL
     names a logo, icon or SVG. The proxy serves photos at the display width
     and never upscales, so a served file narrower than asked is a small
     original. A large opaque photo is a photo at any aspect (portraits too). */
  const artPresentation = (fit, frameRatio, requested = 0, logoUrl = false) => {
    if (!fit.opaque) return 'card'
    const smallOriginal = fit.width < 200 && fit.height < 200 && (!requested || fit.width < requested * 0.95)
    if (smallOriginal) return 'card'
    if (logoUrl && fit.ratio > 0.8 && fit.ratio < 1.25) return 'card'
    if (frameRatio != null && fit.ratio > frameRatio * 1.2) return 'whole'
    return 'photo'
  }
  /* art-classifier:end */
  const requestedWidth = (img) => {
    const m = /[?&]w=(\d+)/.exec(img.currentSrc || img.src || '')
    return m ? Number(m[1]) : 0
  }
  /* A blank or broken image drops its frame; the layout falls back to the no-art shape (a timeline row to its placeholder tile). */
  const dropArt = (fig) => {
    const holder = fig.closest('[data-art-holder]')
    const link = fig.closest('.art-link')
    // A timeline row keeps its thumbnail slot: the placeholder tile under the art shows instead.
    if (holder && holder.dataset.artHolder === 'tlrow') {
      fig.remove()
      holder.classList.remove('has-media')
      return
    }
    ;(link || fig).remove()
    if (holder) {
      holder.classList.remove('has-media')
      if (holder.dataset.artHolder === 'row' && !holder.classList.contains('dense')) holder.classList.add('text-led')
    }
  }
  const presentArt = async (img) => {
    const fig = img.closest('figure.duo')
    if (!fig || fig.dataset.inspected) return
    fig.dataset.inspected = '1'
    if (!img.naturalWidth) {
      dropArt(fig)
      return
    }
    const fit = await inspectArt(img)
    if (!fig.isConnected) return
    if (fit.kind === 'blank') {
      dropArt(fig)
      return
    }
    const frameRatio = fig.clientHeight > 0 ? fig.clientWidth / fig.clientHeight : null
    fig.classList.add('ready')
    const cards = fig.classList.contains('framed')
    let card = false
    if (cards) {
      const presentation = artPresentation(fit, frameRatio, requestedWidth(img), fig.classList.contains('card'))
      card = presentation === 'card'
      fig.classList.toggle('card', card)
      fig.classList.toggle('whole', presentation === 'whole')
      fig.classList.toggle('portrait', presentation === 'photo' && fit.ratio < 0.9)
    } else {
      const contained = !fit.opaque
      fig.classList.toggle('contain', contained)
      fig.classList.toggle('whole', fit.opaque && frameRatio != null && fit.ratio > frameRatio * 1.2)
      const cover = fig.closest('.cover')
      if (cover && !fit.opaque) {
        cover.classList.add('logo')
        cover.classList.remove('has-media')
      }
    }
    if (!fit.opaque && fit.dark && (card || fig.classList.contains('contain'))) fig.classList.add('dark-plate')
  }
  /* Loaded images wait here for an idle slice (at most ~300 ms). */
  const pending = []
  let idleHandle = 0
  const idle = window.requestIdleCallback
    ? (fn) => window.requestIdleCallback(fn, { timeout: 300 })
    : (fn) => setTimeout(() => fn({ timeRemaining: () => 8, didTimeout: true }), 50)
  const drain = (deadline) => {
    idleHandle = 0
    do {
      const img = pending.shift()
      presentArt(img).catch((err) => console.warn('[feed] art inspection failed:', err))
    } while (pending.length && (deadline.timeRemaining() > 4 || deadline.didTimeout))
    if (pending.length) idleHandle = idle(drain)
  }
  const queueArt = (img) => {
    pending.push(img)
    if (!idleHandle) idleHandle = idle(drain)
  }
  const watchArt = (scope = doc) => {
    for (const img of $$('figure.duo img', scope)) {
      if (img.complete) queueArt(img)
      else {
        img.addEventListener('load', () => queueArt(img), { once: true })
        img.addEventListener('error', () => {
          const fig = img.closest('figure.duo')
          if (fig) dropArt(fig)
        }, { once: true })
      }
    }
  }

  doc.body.addEventListener('htmx:afterSwap', (e) => watchArt(e.target))

  /* -- topics: the find box filters the beats in place ---------------------- */
  const find = $('[data-topic-filter]')
  if (find) {
    const rows = $$('[data-topic]')
    const sections = $$('[data-topic-section]')
    const empty = $('[data-topic-empty]')
    find.addEventListener('input', () => {
      const q = find.value.trim().toLowerCase()
      for (const li of rows) li.hidden = !!q && !li.dataset.topic.includes(q)
      let any = false
      for (const s of sections) {
        const shown = $$('[data-topic]', s).some((li) => !li.hidden)
        s.hidden = !shown
        any ||= shown
      }
      if (empty) empty.hidden = any
    })
  }

  /* -- front page: the receipts figure, the mood theme highlight, the fresh pulse --------
     The two rotations advance only while the page is visible and live updates are not paused
     (html[data-motion="paused"]) and the reader does not ask for less motion. */
  const root = doc.documentElement
  const calmer = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null
  const holding = () => root.dataset.motion === 'paused' || !!calmer?.matches || doc.hidden
  /* One item of `root` shown at a time: every `ms`, the current item fades out (0.9 s), the next fades in. */
  const rotate = (root, itemSel, ms) => {
    const items = $$(itemSel, root)
    if (items.length < 2) return
    let shown = 0
    let fading = 0
    setInterval(() => {
      if (fading || holding()) return
      root.classList.add('is-out')
      fading = setTimeout(() => {
        items[shown].hidden = true
        shown = (shown + 1) % items.length
        items[shown].hidden = false
        root.classList.remove('is-out')
        fading = 0
      }, 900)
    }, ms)
  }
  const figures = $('[data-figure-rotor]')
  if (figures) rotate(figures, '[data-figure-item]', 10000)
  const themes = $$('[data-theme-item]')
  if (themes.length > 1) {
    let on = Math.max(0, themes.findIndex((el) => el.classList.contains('is-on')))
    setInterval(() => {
      if (holding()) return
      themes[on].classList.remove('is-on')
      on = (on + 1) % themes.length
      themes[on].classList.add('is-on')
    }, 11000)
  }
  /* A plain swap of the pulse marks its newest bar fresh (data-round changed); the in-place update below animates instead. */
  let lastRound = $('#chain-today')?.dataset.round || ''
  doc.body.addEventListener('htmx:afterSwap', () => {
    const pulse = doc.getElementById('chain-today')
    if (!pulse) return
    const round = pulse.dataset.round || ''
    pulse.dataset.fresh = round && round !== lastRound ? '1' : '0'
    lastRound = round
  })

  /* -- the chain widget's charts: details on hover, tap or focus -------------------------
     Each block bar (.pg-front-bar) and the price line (.pg-front-spark-svg in .pg-front-spark-plot)
     shows a tooltip (.pg-front-tip) in the widget. The tooltip is visual only: it is aria-hidden, since
     each bar and the line carry their own label. The partials are replaced by htmx every few seconds, so
     this listens on the document and keeps an open tooltip on the same round or price point across a swap
     (hiding it when that point is gone), and puts keyboard focus back on the new copy of the part it was on. */
  const within = (e, sel) => (e.target instanceof Element ? e.target.closest(sel) : null)
  const pointsOf = (plot) => $$('.pg-front-spark-points > li', plot)
  const stamp = (ms, options) => {
    try {
      return new Intl.DateTimeFormat(root.lang || undefined, options).format(ms)
    } catch {
      return new Intl.DateTimeFormat(undefined, options).format(ms)
    }
  }
  let tip = null
  let open = null // { chart: 'bar' | 'point', key: round or epoch, anchor: plot for points }
  let restore = null // the focused part of a chart about to be swapped out
  let roving = null // index of the block bar that holds the tab stop; null = the newest
  const fill = (lines) => {
    tip.replaceChildren(
      ...lines
        .filter(Boolean)
        .map((text, i) => {
          const line = doc.createElement('span')
          line.className = i === 0 ? 'pg-front-tip-head' : 'pg-front-tip-line'
          line.textContent = text
          return line
        }),
    )
  }
  /* Above the anchor, clamped to the chart's box (not the widget, so the card heading is never covered).
     When there is no room above inside the chart, it sits just below the anchor's top edge instead. Centred
     on the anchor and kept inside the widget horizontally. The tooltip is positioned physically (left/top),
     so coordinates from getBoundingClientRect map directly in both directions. */
  const place = (box, chart, host) => {
    const frame = host.getBoundingClientRect()
    const ox = frame.left + host.clientLeft
    const oy = frame.top + host.clientTop
    tip.style.left = '0px'
    tip.style.top = '0px'
    const w = tip.offsetWidth
    const left = Math.min(Math.max(box.left + box.width / 2 - ox - w / 2, 0), Math.max(host.clientWidth - w, 0))
    tip.style.left = `${left}px`
    const h = tip.offsetHeight // measured at the final left, so the text wraps as it will be shown
    const gap = 6
    const chartTop = chart.top - oy
    const chartBottom = chart.bottom - oy
    const above = box.top - oy - h - gap
    const below = box.top - oy + gap
    const top = Math.min(Math.max(above >= chartTop ? above : below, chartTop), Math.max(chartBottom - h, chartTop))
    tip.style.top = `${top}px`
  }
  const show = (host, anchor, chart, lines, state) => {
    if (!tip) {
      tip = doc.createElement('div')
      tip.className = 'px-pop pg-front-tip'
      tip.setAttribute('aria-hidden', 'true')
    }
    if (tip.parentElement !== host) host.append(tip)
    fill(lines)
    open = state
    tip.classList.add('is-open')
    place(anchor.getBoundingClientRect(), chart.getBoundingClientRect(), host)
  }
  /* Forgets the open tooltip's target: a price point's crosshair goes with it. */
  const clearOpen = () => {
    if (open?.chart === 'point') {
      for (const mark of open.anchor.querySelectorAll('.pg-front-spark-rule, .pg-front-spark-dot')) {
        mark.hidden = true
      }
    }
    open = null
  }
  const hide = () => {
    tip?.classList.remove('is-open')
    clearOpen()
  }
  /* The block bars are one tab stop: the bar with tabindex 0 takes Tab; the arrows and Home/End move between bars. */
  const barsOf = () => $$('#chain-today .pg-front-bar')
  const setRoving = (bars, index) => {
    bars.forEach((b, i) => {
      b.tabIndex = i === index ? 0 : -1
    })
  }
  const rove = (index, focus) => {
    const bars = barsOf()
    if (!bars.length) return
    const i = Math.min(Math.max(index, 0), bars.length - 1)
    roving = i
    setRoving(bars, i)
    if (focus) bars[i].focus()
  }
  const showBar = (bar) => {
    const host = bar.closest('.pg-front-widget')
    if (!host) return
    clearOpen()
    const ts = Number(bar.dataset.ts)
    show(
      host,
      bar.querySelector('.px-bar') || bar,
      bar.closest('.pg-front-pulse') || host,
      [
        bar.dataset.tipHead,
        bar.dataset.tipTxns,
        bar.dataset.tipMix,
        ts ? stamp(ts * 1000, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '',
      ],
      { chart: 'bar', key: bar.dataset.round },
    )
  }
  const marksOf = (plot) => {
    for (const cls of ['pg-front-spark-rule', 'pg-front-spark-dot']) {
      if (!plot.querySelector(`.${cls}`)) {
        const mark = doc.createElement('span')
        mark.className = cls
        mark.setAttribute('aria-hidden', 'true')
        mark.hidden = true
        plot.append(mark)
      }
    }
    return { rule: plot.querySelector('.pg-front-spark-rule'), dot: plot.querySelector('.pg-front-spark-dot') }
  }
  const showPoint = (plot, index) => {
    const host = plot.closest('.pg-front-widget')
    const pts = pointsOf(plot)
    if (!host || !pts.length) return
    const i = Math.min(Math.max(index, 0), pts.length - 1)
    const p = pts[i]
    clearOpen()
    const { rule, dot } = marksOf(plot)
    rule.style.left = `${p.dataset.x}%`
    dot.style.left = `${p.dataset.x}%`
    dot.style.top = `${p.dataset.y}%`
    rule.hidden = false
    dot.hidden = false
    plot.dataset.index = String(i)
    show(
      host,
      dot,
      plot,
      [
        p.dataset.price,
        stamp(Number(p.dataset.epoch) * 1000, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
      ],
      { chart: 'point', key: p.dataset.epoch, anchor: plot },
    )
  }
  /* The point nearest the pointer's x, moved to only when it changes. */
  const pointFromPointer = (plot, clientX) => {
    const box = plot.getBoundingClientRect()
    const n = pointsOf(plot).length
    if (!n || !box.width) return
    const i = Math.round(Math.min(Math.max((clientX - box.left) / box.width, 0), 1) * (n - 1))
    if (open?.chart === 'point' && open.anchor === plot && plot.dataset.index === String(i)) return
    showPoint(plot, i)
  }

  /* Puts keyboard focus and the tab stop back on the chart after its bars changed: the reader's block keeps them
     while it is shown (the window slides by a block per round), else the same position. An open tooltip is shown
     again on its block (`key`, a round) or hidden when that block is gone. */
  const settleChain = (had, key) => {
    const bars = barsOf()
    if (bars.length) {
      const same = had?.round ? bars.findIndex((b) => b.dataset.round === had.round) : -1
      const i = same >= 0 ? same : roving == null ? bars.length - 1 : Math.min(roving, bars.length - 1)
      setRoving(bars, i)
      if (had?.id === 'chain-today' && had.bar >= 0) bars[i].focus({ preventScroll: true })
    }
    if (key != null) {
      const next = doc.querySelector(`#chain-today .pg-front-bar[data-round="${key}"]`)
      if (next) showBar(next)
      else hide()
    }
  }
  /* -- the chain pulse, in place ---------------------------------------------------
     htmx would replace #chain-today on every poll, so all 20 bars would jump in one frame. Its beforeSwap
     hands the response to patchChain instead (shouldSwap off). The bars already shown are updated in place
     (height, label, tooltip data). When the window has moved on by at most PULSE_MAX_NEW blocks, the new bars
     are added past the chart's edge and the track slides them in over SLIDE.duration; then the oldest bars are
     dropped. Paused or reduced-motion readers get the same result at once. A response that does not line up
     with the bars shown returns false, and htmx swaps it in whole. */
  const PULSE_SLOTS = 20 // live.py PULSE_WINDOW: the bars a window holds
  const PULSE_MAX_NEW = 5
  const SLIDE = { duration: 2000, easing: 'cubic-bezier(.45,.05,.35,1)' } // chain.css: the bars' height transition uses the same
  const BAR_ATTRS = ['data-round', 'data-tip-head', 'data-tip-txns', 'data-tip-mix', 'data-ts', 'aria-label']
  let slide = null // { end } of the running slide; a newer response ends it first
  const barsIn = (scope) => $$('.pg-front-bar', scope)
  const roundOf = (bar) => Number(bar.dataset.round)
  const syncBar = (bar, src) => {
    for (const name of BAR_ATTRS) {
      if (src.hasAttribute(name)) bar.setAttribute(name, src.getAttribute(name))
      else bar.removeAttribute(name)
    }
    bar.querySelector('.px-bar').style.blockSize = src.querySelector('.px-bar').style.blockSize
  }
  const markNewest = (bars) => {
    bars.forEach((bar, i) => bar.querySelector('.px-bar').classList.toggle('px-bar--now', i === bars.length - 1))
  }
  /* Applies a chain response to the chart `chart`; false when the response does not line up with its bars. */
  const patchChain = (chart, html, had) => {
    slide?.end()
    const incoming = new DOMParser().parseFromString(html, 'text/html').getElementById('chain-today')
    const track = $('.pg-front-track', chart)
    const cur = track ? barsIn(track) : []
    const next = incoming ? barsIn(incoming) : []
    if (!track || cur.length !== PULSE_SLOTS || next.length !== PULSE_SLOTS) return false
    const last = roundOf(cur[cur.length - 1])
    if (roundOf(next[next.length - 1]) < last) return true // older than the bars shown: keep them
    const k = next.filter((bar) => roundOf(bar) > last).length
    if (k > PULSE_MAX_NEW || !cur.slice(k).every((bar, i) => roundOf(bar) === roundOf(next[i]))) return false

    const reopen = open?.chart === 'bar' ? open.key : null
    if (reopen != null) hide()
    chart.dataset.round = incoming.dataset.round
    chart.dataset.fresh = '0'
    lastRound = chart.dataset.round
    $('.pg-front-bars', chart).setAttribute('aria-label', $('.pg-front-bars', incoming).getAttribute('aria-label'))
    const note = $('.px-note', chart)
    const incomingNote = $('.px-note', incoming)
    if (note && incomingNote) note.textContent = incomingNote.textContent

    const animate = k > 0 && !holding()
    let anim = null
    let ended = false
    const end = () => {
      if (ended) return
      ended = true
      anim?.finish()
      anim?.cancel()
      if (slide === handle) slide = null
      barsIn(track).slice(0, k).forEach((bar) => bar.remove())
      delete track.dataset.sliding
      settleChain(had, reopen)
    }
    const handle = { end }
    if (animate) track.dataset.sliding = '' // the bars hold their slot while the track moves
    const clones = next.slice(PULSE_SLOTS - k).map((src) => {
      const bar = doc.importNode(src, true)
      bar.tabIndex = -1
      bar.querySelector('.px-bar').style.blockSize = '0%'
      return bar
    })
    track.append(...clones)
    if (animate) void track.offsetWidth // commits the new bars at height 0, so they rise
    barsIn(track).slice(k).forEach((bar, i) => syncBar(bar, next[i]))
    markNewest(barsIn(track).slice(k))
    if (!animate) {
      end()
      return true
    }
    const dir = root.dir === 'rtl' ? 1 : -1
    const slot = track.getBoundingClientRect().width / PULSE_SLOTS
    anim = track.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${dir * k * slot}px)` }], {
      ...SLIDE,
      fill: 'forwards',
    })
    anim.onfinish = end
    anim.oncancel = end
    slide = handle
    return true
  }

  doc.addEventListener('pointerover', (e) => {
    if (e.pointerType === 'touch') return
    const bar = within(e, '.pg-front-bar')
    if (bar) return showBar(bar)
    const plot = within(e, '.pg-front-spark-plot')
    if (plot) pointFromPointer(plot, e.clientX)
  })
  doc.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'touch') return
    const plot = within(e, '.pg-front-spark-plot')
    if (plot) pointFromPointer(plot, e.clientX)
  })
  doc.addEventListener('pointerout', (e) => {
    if (e.pointerType === 'touch') return
    const from = within(e, '.pg-front-bar, .pg-front-spark-plot')
    if (from && !from.contains(e.relatedTarget)) hide()
  })
  doc.addEventListener('pointerdown', (e) => {
    const bar = within(e, '.pg-front-bar')
    if (bar) return showBar(bar)
    const plot = within(e, '.pg-front-spark-plot')
    if (plot) return pointFromPointer(plot, e.clientX)
    if (open) hide()
  })
  doc.addEventListener('focusin', (e) => {
    const bar = within(e, '.pg-front-bar')
    if (bar) {
      roving = barsOf().indexOf(bar)
      setRoving(barsOf(), roving)
      return showBar(bar)
    }
    const svg = within(e, '.pg-front-spark-svg')
    if (svg) showPoint(svg.parentElement, pointsOf(svg.parentElement).length - 1)
  })
  doc.addEventListener('focusout', (e) => {
    if (within(e, '.pg-front-bar, .pg-front-spark-svg')) hide()
  })
  doc.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (open) hide()
      return
    }
    const bar = within(e, '.pg-front-bar')
    if (bar) {
      const bars = barsOf()
      const cur = bars.indexOf(bar)
      if (cur < 0) return
      // Visual order: in a right-to-left page the first block sits on the right, so ArrowLeft goes to the next one.
      const rtl = root.dir === 'rtl'
      const next = { ArrowLeft: rtl ? cur + 1 : cur - 1, ArrowRight: rtl ? cur - 1 : cur + 1, Home: 0, End: bars.length - 1 }[e.key]
      if (next === undefined) return
      e.preventDefault()
      if (next >= 0 && next < bars.length) rove(next, true)
      return
    }
    const svg = within(e, '.pg-front-spark-svg')
    if (!svg) return
    const plot = svg.parentElement
    const last = pointsOf(plot).length - 1
    const cur = Number(plot.dataset.index ?? last)
    const next = { ArrowLeft: cur - 1, ArrowRight: cur + 1, Home: 0, End: last }[e.key]
    if (next === undefined) return
    e.preventDefault()
    showPoint(plot, next)
  })
  doc.body.addEventListener('htmx:beforeSwap', (e) => {
    const act = doc.activeElement
    restore =
      act && act !== e.target && e.target.contains(act)
        ? {
            id: e.target.id,
            bar: act.classList.contains('pg-front-bar') ? barsOf().indexOf(act) : -1,
            round: act.classList.contains('pg-front-bar') ? act.dataset.round : null,
            spark: act.classList.contains('pg-front-spark-svg'),
          }
        : null
    if (e.target.id === 'chain-today' && !e.detail.isError && patchChain(e.target, e.detail.serverResponse || '', restore)) {
      restore = null
      e.detail.shouldSwap = false
    }
  })
  doc.body.addEventListener('htmx:afterSwap', (e) => {
    const had = restore
    restore = null
    if (e.target.id === 'chain-today') {
      settleChain(had, open?.chart === 'bar' ? open.key : null)
    } else if (e.target.id === 'chain-spark') {
      if (had?.id === 'chain-spark' && had.spark) {
        doc.querySelector('#chain-spark .pg-front-spark-svg')?.focus({ preventScroll: true })
      }
      if (open?.chart === 'point') {
        const li = doc.querySelector(`#chain-spark .pg-front-spark-points > li[data-epoch="${open.key}"]`)
        if (li) showPoint(li.closest('.pg-front-spark-plot'), pointsOf(li.closest('.pg-front-spark-plot')).indexOf(li))
        else hide()
      }
    }
  })

  watchArt()
})()
